import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Globe2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHero } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';

const certifications = [
  {
    icon: '🏛️',
    title: 'GACC Registration',
    titleTh: 'การขึ้นทะเบียน GACC',
    desc: 'General Administration of Customs of China (GACC) registration for orchards and packing houses, enabling direct fruit exports to China.',
    descTh: 'การขึ้นทะเบียนกับสำนักงานศุลกากรแห่งชาติจีน (GACC) สำหรับสวนผลไม้และโรงคัดบรรจุ ช่วยให้ส่งออกผลไม้โดยตรงสู่จีน',
    badge: 'GACC Certified',
  },
  {
    icon: '🌿',
    title: 'Phytosanitary Certificate',
    titleTh: 'ใบรับรองสุขอนามัยพืช',
    desc: 'Thai Department of Agriculture phytosanitary certification ensuring all fruit exports meet international plant health standards.',
    descTh: 'การรับรองสุขอนามัยพืชจากกรมวิชาการเกษตรไทย รับประกันว่าการส่งออกผลไม้ทั้งหมดตรงตามมาตรฐานสุขภาพพืชสากล',
    badge: 'DOA Certified',
  },
  {
    icon: '🏥',
    title: 'Thai FDA Registration',
    titleTh: 'การขึ้นทะเบียน อย. ไทย',
    desc: 'All medical devices distributed by Yaruwat are registered with the Thai Food and Drug Administration (FDA).',
    descTh: 'อุปกรณ์การแพทย์ทั้งหมดที่จัดจำหน่ายโดยยารุวาทขึ้นทะเบียนกับสำนักงานคณะกรรมการอาหารและยา (อย.) ไทย',
    badge: 'Thai FDA Registered',
  },
  {
    icon: '✅',
    title: 'CE Marking',
    titleTh: 'เครื่องหมาย CE',
    desc: 'Medical devices carry CE marking confirming compliance with European health, safety, and environmental protection standards.',
    descTh: 'อุปกรณ์การแพทย์มีเครื่องหมาย CE ยืนยันการปฏิบัติตามมาตรฐานสุขภาพ ความปลอดภัย และการคุ้มครองสิ่งแวดล้อมยุโรป',
    badge: 'CE Marked',
  },
  {
    icon: '☕',
    title: 'Food Safety Standards',
    titleTh: 'มาตรฐานความปลอดภัยอาหาร',
    desc: 'Coffee products comply with HACCP, GMP, and international food safety standards for export to China and global markets.',
    descTh: 'ผลิตภัณฑ์กาแฟเป็นไปตาม HACCP, GMP และมาตรฐานความปลอดภัยอาหารระหว่างประเทศสำหรับส่งออกสู่จีนและตลาดโลก',
    badge: 'HACCP & GMP',
  },
  {
    icon: '🌐',
    title: 'ISO Standards',
    titleTh: 'มาตรฐาน ISO',
    desc: 'Operations aligned with ISO quality management standards ensuring consistent quality across all business units.',
    descTh: 'การดำเนินงานสอดคล้องกับมาตรฐานการจัดการคุณภาพ ISO รับประกันคุณภาพที่สม่ำเสมอในทุกหน่วยธุรกิจ',
    badge: 'ISO Aligned',
  },
];

const partners = [
  {
name: 'Xianpinda ()',
    country: 'China',
    countryTh: 'จีน',
    desc: 'Strategic distribution partner in China for Thai fruits and food products. Xianpinda provides market access, distribution networks, and consumer insights for the Chinese market.',
    descTh: 'พันธมิตรด้านการจัดจำหน่ายเชิงกลยุทธ์ในจีนสำหรับผลไม้ไทยและผลิตภัณฑ์อาหาร Xianpinda ให้การเข้าถึงตลาด เครือข่ายการจัดจำหน่าย และข้อมูลเชิงลึกด้านผู้บริโภคสำหรับตลาดจีน',
    icon: '🇨🇳',
    type: 'Distribution Partner',
    typeTh: 'พันธมิตรด้านการจัดจำหน่าย',
  },
  {
    name: 'GACC-Registered Orchards',
    country: 'Thailand',
    countryTh: 'ไทย',
    desc: 'Network of GACC-registered fruit orchards across Thailand\'s major fruit-growing regions including Chanthaburi, Rayong, and the Eastern Economic Corridor.',
    descTh: 'เครือข่ายสวนผลไม้ที่ขึ้นทะเบียน GACC ทั่วพื้นที่ปลูกผลไม้หลักของประเทศไทย รวมถึงจันทบุรี ระยอง และเขตเศรษฐกิจพิเศษภาคตะวันออก',
    icon: '🌳',
    type: 'Supply Chain Partner',
    typeTh: 'พันธมิตรห่วงโซ่อุปทาน',
  },
  {
    name: 'Certified Packing Houses',
    country: 'Thailand',
    countryTh: 'ไทย',
    desc: 'State-of-the-art certified packing facilities meeting GACC, Thai DOA, and international export requirements for fruits destined for the Chinese market.',
    descTh: 'โรงคัดบรรจุที่ได้รับการรับรองทันสมัยตรงตามข้อกำหนด GACC, กรมวิชาการเกษตรไทย และการส่งออกระหว่างประเทศสำหรับผลไม้ที่มุ่งสู่ตลาดจีน',
    icon: '🏭',
    type: 'Processing Partner',
    typeTh: 'พันธมิตรด้านการแปรรูป',
  },
];

export default function Partners() {
  const { t } = useLanguage();

  return (
    <Layout>
      <PageHero
        title="Partners & Compliance"
        titleTh="พันธมิตรและการรับรอง"
        subtitle="Certified, compliant, and trusted across borders"
        subtitleTh="ได้รับการรับรอง ปฏิบัติตามข้อกำหนด และเชื่อถือได้ข้ามพรมแดน"
        image="/images/hero_7.jpeg"
      />

      {/* Compliance Overview */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-4"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Shield className="w-4 h-4" />
              {t('Regulatory Compliance', 'การปฏิบัติตามข้อกำหนด')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('Our Certifications', 'ใบรับรองของเรา')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                'Yaruwat maintains strict compliance with all relevant Thai, Chinese, and international regulations across its business operations.',
                'ยารุวาทรักษาการปฏิบัติตามข้อกำหนดอย่างเคร่งครัดกับข้อบังคับไทย จีน และนานาชาติที่เกี่ยวข้องทั้งหมดในการดำเนินธุรกิจ'
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {certifications.map((cert, i) => (
              <motion.div
                key={cert.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-6 shadow-md border border-border/50 hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{cert.icon}</div>
                <div className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {cert.badge}
                </div>
                <h3 className="font-bold text-foreground mb-2">{t(cert.title, cert.titleTh)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(cert.desc, cert.descTh)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Globe2 className="w-4 h-4" />
              {t('Our Network', 'เครือข่ายของเรา')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('Key Partners', 'พันธมิตรหลัก')}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {partners.map((partner, i) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-md border border-border/50"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="text-6xl flex-shrink-0">{partner.icon}</div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-foreground">{partner.name}</h3>
                      <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                        {t(partner.type, partner.typeTh)}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        📍 {t(partner.country, partner.countryTh)}
                      </span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{t(partner.desc, partner.descTh)}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GACC Detail */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-6xl mb-6">🏛️</div>
            <h2 className="text-3xl font-bold mb-4">
              {t('GACC Registered — Direct Access to China', 'ขึ้นทะเบียน GACC — เข้าถึงจีนโดยตรง')}
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-6 max-w-2xl mx-auto">
              {t(
                'Our GACC-registered orchards and packing houses provide direct, compliant access to the Chinese fresh fruit market — one of the largest and fastest-growing in the world.',
                'สวนผลไม้และโรงคัดบรรจุที่ขึ้นทะเบียน GACC ของเราให้การเข้าถึงตลาดผลไม้สดจีนโดยตรงและสอดคล้องกับข้อกำหนด ซึ่งเป็นหนึ่งในตลาดที่ใหญ่ที่สุดและเติบโตเร็วที่สุดในโลก'
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                { icon: '🌳', label: 'Registered Orchards', labelTh: 'สวนผลไม้ที่ขึ้นทะเบียน', value: '50+' },
                { icon: '🏭', label: 'Packing Houses', labelTh: 'โรงคัดบรรจุ', value: '10+' },
                { icon: '📦', label: 'Fruit Varieties', labelTh: 'พันธุ์ผลไม้', value: '8+' },
              ].map((stat) => (
                <div key={stat.label} className="bg-primary-foreground/10 rounded-xl p-6">
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold mb-1">{stat.value}</div>
                  <div className="text-primary-foreground/70 text-sm">{t(stat.label, stat.labelTh)}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
